"use client";

import { Check, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import { cn } from "@/app/_module/lib/utils";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Label } from "@/app/_module/components/ui/label";
import { Switch } from "@/app/_module/components/ui/switch";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useNotificationSoundPreference } from "@/app/_module/hooks/use-notification-sound";
import { notificationPreferencesService } from "../../services/notificationPreferencesService";
import {
  NotificationPreferences,
  UpdateNotificationPreferencesRequest,
} from "../../types/NotificationPreferences";

export interface NotificationOption {
  id: string;
  label: string;
  description: string;
  backendKey?: keyof NotificationPreferences;
  defaultValue?: boolean;
  isAlwaysOn?: boolean;
  dependsOn?: string;
  /**
   * Gate for switching this option OFF, owned by the PARENT.
   *
   * Returns true to proceed, false to abandon (switch snaps back). Used where
   * "off" removes a capability from OTHER people — they get no notification,
   * they just find the button gone — so the parent can render whatever
   * confirmation or scope picker that decision needs.
   */
  interceptDisable?: () => Promise<boolean>;
  /**
   * Inline panel rendered directly beneath this option's row.
   *
   * Receives the switch's LIVE checked state. Deliberately a function rather
   * than a node: deriving visibility from a parent prop meant the panel waited
   * on a save round-trip, so flipping the switch off left it hidden until the
   * server answered — and stayed hidden entirely if the save failed.
   */
  renderBelow?: (checked: boolean) => React.ReactNode;
}

interface ToggleSettingsProps {
  title?: string;
  description?: string;
  icon?: React.ReactNode;
  options: NotificationOption[];
  onSave?: (settings: Record<string, boolean>, type: string) => void | Promise<void>;
  saveButtonText?: string;
  className?: string;
  type?: string;
  useBackendIntegration?: boolean;
  agencyLeaderboardEnabled?: boolean;
  agencyName?: string;
  isAgencyOwner?: boolean;
  allowAgentExport?: boolean;
}

// ── Animation variants ────────────────────────────────────────
const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.05 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE },
  },
};

const ToggleSettings = function ({
  title = "Notification Preferences",
  description = "Configure how and when you receive notifications",
  icon,
  options,
  onSave,
  className,
  type = "settings",
  useBackendIntegration = false,
  agencyLeaderboardEnabled = true,
  agencyName = "Agency",
  isAgencyOwner = false,
  allowAgentExport = true,
}: ToggleSettingsProps) {
  const [notifications, setNotifications] = useState<Record<string, boolean>>({});
  const [originalNotifications, setOriginalNotifications] = useState<Record<string, boolean>>({});
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const hasInitializedRef = useRef(false);
  const hasFetchedFromApiRef = useRef(false);
  const isSavingRef = useRef(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userHasToggledRef = useRef(false);
  const notificationsRef = useRef<Record<string, boolean>>(notifications);
  const pendingChangesRef = useRef<Record<string, boolean>>({});

  const { toast } = useToast();
  const { user, refetchUser } = useUserContext();
  const { setSoundPreference } = useNotificationSoundPreference();

  // ── Initialize state from user data ──
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!user) return;

    const newNotifications: Record<string, boolean> = {};

    if (useBackendIntegration) {
      const prefs = user?.notificationPreferences;
      options.forEach((option) => {
        if (option.backendKey && prefs) {
          const value = prefs[option.backendKey];
          newNotifications[option.id] = typeof value === "boolean" ? value : (option.defaultValue ?? true);
        } else {
          newNotifications[option.id] = option.defaultValue ?? true;
        }
      });
    } else {
      const privacy = user?.user?.privacySettings;
      options.forEach((option) => {
        if (option.id === "profileVisibility") {
          newNotifications[option.id] =
            privacy?.profileVisibility !== undefined
              ? privacy.profileVisibility === "public"
              : (option.defaultValue ?? true);
        } else if (option.id === "analyticsTracking") {
          newNotifications[option.id] = privacy?.dataSharing?.analytics ?? (option.defaultValue ?? true);
        } else if (option.id === "showInLeaderboard") {
          newNotifications[option.id] = user?.user?.showOnLeaderboard ?? (option.defaultValue ?? true);
        } else if (option.id === "hideNameOnLeaderboard") {
          newNotifications[option.id] = user?.user?.hideNameOnLeaderboard ?? (option.defaultValue ?? false);
        } else if (option.id === "enableLeaderboardForAgents") {
          newNotifications[option.id] = agencyLeaderboardEnabled;
        } else if (option.id === "allowAgentExport") {
          newNotifications[option.id] = allowAgentExport;
        } else {
          newNotifications[option.id] = option.defaultValue ?? true;
        }
      });
    }

    hasInitializedRef.current = true;
    notificationsRef.current = newNotifications;
    setNotifications(newNotifications);
    setOriginalNotifications({ ...newNotifications });
  }, [user, options, useBackendIntegration, agencyLeaderboardEnabled, allowAgentExport]);

  // ── Fetch fresh notification preferences ──
  useEffect(() => {
    if (!useBackendIntegration) return;

    let cancelled = false;
    notificationPreferencesService
      .getNotificationPreferences()
      .then((prefs) => {
        if (cancelled || userHasToggledRef.current) return;
        const updated: Record<string, boolean> = {};
        options.forEach((option) => {
          if (option.backendKey && prefs) {
            const value = (prefs as any)[option.backendKey];
            updated[option.id] = typeof value === "boolean" ? value : (option.defaultValue ?? true);
          } else {
            updated[option.id] = option.defaultValue ?? true;
          }
        });
        hasFetchedFromApiRef.current = true;
        notificationsRef.current = updated;
        setNotifications(updated);
        setOriginalNotifications({ ...updated });
        refetchUser((prev) => ({ ...prev, notificationPreferences: prefs }));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBackendIntegration]);

  // ── Sync settings when they change ──
  useEffect(() => {
    if (!hasInitializedRef.current || userHasToggledRef.current || hasFetchedFromApiRef.current) return;

    setNotifications((prev) => {
      const updated = { ...prev };
      if ("enableLeaderboardForAgents" in prev) updated.enableLeaderboardForAgents = agencyLeaderboardEnabled;
      if ("allowAgentExport" in prev) updated.allowAgentExport = allowAgentExport;

      if (useBackendIntegration && user?.notificationPreferences) {
        const prefs = user.notificationPreferences;
        options.forEach((option) => {
          if (option.backendKey && option.id in prev) {
            const value = prefs[option.backendKey];
            if (typeof value === "boolean") updated[option.id] = value;
          }
        });
      }

      if (!useBackendIntegration && user?.user) {
        if ("showInLeaderboard" in prev && user.user.showOnLeaderboard !== undefined)
          updated.showInLeaderboard = user.user.showOnLeaderboard;
        if ("hideNameOnLeaderboard" in prev && user.user.hideNameOnLeaderboard !== undefined)
          updated.hideNameOnLeaderboard = user.user.hideNameOnLeaderboard;
        if ("profileVisibility" in prev && user.user.privacySettings?.profileVisibility !== undefined)
          updated.profileVisibility = user.user.privacySettings.profileVisibility === "public";
        if ("analyticsTracking" in prev && user.user.privacySettings?.dataSharing?.analytics !== undefined)
          updated.analyticsTracking = user.user.privacySettings.dataSharing.analytics;
      }

      notificationsRef.current = updated;
      return updated;
    });

    setOriginalNotifications((prev) => {
      const updated = { ...prev };
      if ("enableLeaderboardForAgents" in prev) updated.enableLeaderboardForAgents = agencyLeaderboardEnabled;
      if ("allowAgentExport" in prev) updated.allowAgentExport = allowAgentExport;

      if (useBackendIntegration && user?.notificationPreferences) {
        const prefs = user.notificationPreferences;
        options.forEach((option) => {
          if (option.backendKey && option.id in prev) {
            const value = prefs[option.backendKey];
            if (typeof value === "boolean") updated[option.id] = value;
          }
        });
      }

      if (!useBackendIntegration && user?.user) {
        if ("showInLeaderboard" in prev && user.user.showOnLeaderboard !== undefined)
          updated.showInLeaderboard = user.user.showOnLeaderboard;
        if ("hideNameOnLeaderboard" in prev && user.user.hideNameOnLeaderboard !== undefined)
          updated.hideNameOnLeaderboard = user.user.hideNameOnLeaderboard;
        if ("profileVisibility" in prev && user.user.privacySettings?.profileVisibility !== undefined)
          updated.profileVisibility = user.user.privacySettings.profileVisibility === "public";
        if ("analyticsTracking" in prev && user.user.privacySettings?.dataSharing?.analytics !== undefined)
          updated.analyticsTracking = user.user.privacySettings.dataSharing.analytics;
      }

      return updated;
    });
  }, [
    agencyLeaderboardEnabled,
    allowAgentExport,
    user?.notificationPreferences,
    user?.user?.showOnLeaderboard,
    user?.user?.hideNameOnLeaderboard,
    user?.user?.privacySettings,
    useBackendIntegration,
    options,
  ]);

  // ── Handle toggle ──
  const applyToggle = (id: string, checked: boolean) => {
    userHasToggledRef.current = true;

    const option = options.find((o) => o.id === id);
    if (option?.backendKey) {
      pendingChangesRef.current[option.backendKey] = checked;
    }

    const updated = { ...notificationsRef.current, [id]: checked };
    notificationsRef.current = updated;
    setNotifications(updated);

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      performSave({ ...notificationsRef.current });
    }, 800);

    if (id === "notificationSound") setSoundPreference(checked);
  };

  const handleToggle = (id: string, checked: boolean) => {
    const option = options.find((o) => o.id === id);
    // Only switching OFF is gated — restoring access needs no ceremony.
    if (!checked && option?.interceptDisable) {
      void (async () => {
        const proceed = await option.interceptDisable!();
        if (proceed) applyToggle(id, false);
        else setNotifications({ ...notificationsRef.current }); // snap back
      })();
      return;
    }
    applyToggle(id, checked);
  };

  // ── Perform save ──
  const performSave = async (settingsToSave: Record<string, boolean>) => {
    if (!userHasToggledRef.current) return;
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    setSaveStatus("saving");

    try {
      if (useBackendIntegration && type === "notifications") {
        const backendUpdate: UpdateNotificationPreferencesRequest = {
          ...pendingChangesRef.current,
        } as UpdateNotificationPreferencesRequest;
        pendingChangesRef.current = {};

        await notificationPreferencesService.updateNotificationPreferences(backendUpdate);
        refetchUser((prev) => ({
          ...prev,
          notificationPreferences: prev.notificationPreferences
            ? { ...prev.notificationPreferences, ...backendUpdate }
            : undefined,
        }));
      } else if (onSave) {
        await onSave(settingsToSave, type);
      }

      setOriginalNotifications({ ...settingsToSave });
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      setSaveStatus("error");
      toast({
        title: "Error Saving Settings",
        description: error instanceof Error ? error.message : "Failed to save settings. Please try again.",
        variant: "destructive",
      });
      setTimeout(() => setSaveStatus("idle"), 3000);
    } finally {
      isSavingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  return (
    <Card className={cn("overflow-hidden", className)}>
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="px-6 pt-6 pb-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
              {icon}
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
              <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
            </div>
          </div>

          {/* Save status pill */}
          <AnimatePresence mode="wait">
            {saveStatus === "saving" && (
              <motion.div
                key="saving"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-xs text-muted-foreground"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving
              </motion.div>
            )}
            {saveStatus === "saved" && (
              <motion.div
                key="saved"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-xs text-emerald-700 border border-emerald-200"
              >
                <Check className="h-3 w-3" />
                Saved
              </motion.div>
            )}
            {saveStatus === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-destructive/10 text-xs text-destructive border border-destructive/20"
              >
                Failed to save
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Gradient divider */}
        <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
      </motion.div>

      {/* ── Toggle Rows ── */}
      <CardContent className="pt-0 pb-6">
        {/* Agency leaderboard alert */}
        {isAgencyOwner &&
          "enableLeaderboardForAgents" in notifications &&
          originalNotifications.enableLeaderboardForAgents !== notifications.enableLeaderboardForAgents && (
            <div className="mb-4 p-3 rounded-lg border-l-2 border-l-primary bg-primary/5">
              <p className="text-xs text-foreground/70 leading-relaxed">
                <strong>Note:</strong> When{" "}
                {notifications.enableLeaderboardForAgents ? "enabled" : "disabled"}, all agents in{" "}
                {agencyName} will be{" "}
                {notifications.enableLeaderboardForAgents
                  ? "able to view and participate in"
                  : "unable to view"}{" "}
                the leaderboard. Individual agents can still opt-out.
              </p>
            </div>
          )}

        <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-3">
          {options.map((option) => {
            if (option.dependsOn && !notifications[option.dependsOn]) return null;

            const isDisabled = saveStatus === "saving" || option.isAlwaysOn;
            const isChecked = option.isAlwaysOn ? true : notifications[option.id] || false;
            const isDependentRow = !!option.dependsOn;

            return (
              <motion.div key={option.id} variants={fadeUp}>
                <div
                  className={cn(
                    "relative rounded-xl border transition-all duration-300",
                    "hover:shadow-md hover:shadow-black/[0.03]",
                    isDependentRow && "ml-6",
                    isChecked && !option.isAlwaysOn
                      ? "border-primary/20 bg-primary/[0.015]"
                      : "border-border",
                    option.isAlwaysOn && "opacity-60"
                  )}
                >
                  {/* Active accent bar */}
                  <div
                    className={cn(
                      "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                      isChecked && !option.isAlwaysOn ? "bg-primary" : "bg-transparent"
                    )}
                  />

                  <div className="flex items-center justify-between p-4 pl-5">
                    <div className="pr-4">
                      <Label
                        className={cn(
                          "text-[15px] font-semibold leading-none",
                          isDisabled ? "text-muted-foreground cursor-not-allowed" : "cursor-pointer"
                        )}
                      >
                        {option.label}
                      </Label>
                      <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                        {option.description}
                      </p>
                    </div>
                    <Switch
                      checked={isChecked}
                      onCheckedChange={(checked) => handleToggle(option.id, checked)}
                      disabled={isDisabled}
                    />
                  </div>
                  {option.renderBelow?.(isChecked)}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </CardContent>

    </Card>
  );
};

export default ToggleSettings;
